import type { CSSProperties } from "react";

/** Presets armónicos min/max (ch) para controles de formulario page */
export const FIELD_WIDTH_PRESETS = {
  xs: { min: 14, max: 22 },
  sm: { min: 18, max: 30 },
  md: { min: 20, max: 38 },
  lg: { min: 24, max: 48 },
  full: { min: 28, max: 64 },
} as const;

export type FieldWidthSize = keyof typeof FIELD_WIDTH_PRESETS;

const PADDING_CH = 8;
const SELECT_EXTRA_CH = 4;

type FieldWidthOptions = {
  size?: FieldWidthSize;
  padding?: number;
  /** Espacio extra para chevron en selects */
  select?: boolean;
};

function resolveFieldWidthCh(
  placeholder: string | undefined,
  label: string | undefined,
  options?: FieldWidthOptions
): { minCh: number; maxCh: number } {
  const size = options?.size ?? "md";
  const preset = FIELD_WIDTH_PRESETS[size];
  const padding = options?.padding ?? PADDING_CH;
  const selectExtra = options?.select ? SELECT_EXTRA_CH : 0;
  const reference = placeholder?.trim() || label?.trim() || "";
  const contentCh = reference.length + padding + selectExtra;

  const minCh = Math.min(preset.max, Math.max(preset.min, contentCh));
  const maxCh = preset.max + selectExtra;

  return {
    minCh: Math.min(minCh, maxCh),
    maxCh,
  };
}

/** Clase responsive: 100% ancho en mobile; presets `ch` desde `sm` */
export const FORM_FIELD_WIDTH_CLASS = "form-field-width";

/**
 * Variables CSS para `.form-field-width` (ver theme.css).
 * Mobile: ancho máximo del contenedor. Desktop: min/max en `ch`.
 */
export function getFieldWidthStyle(
  placeholder?: string,
  label?: string,
  options?: FieldWidthOptions
): CSSProperties {
  const size = options?.size ?? "md";
  const { minCh, maxCh } = resolveFieldWidthCh(placeholder, label, options);

  if (size === "full") {
    return {
      "--field-min-ch": `${FIELD_WIDTH_PRESETS.full.min}ch`,
      "--field-max-ch": `${FIELD_WIDTH_PRESETS.full.max}ch`,
    } as CSSProperties;
  }

  return {
    "--field-min-ch": `${minCh}ch`,
    "--field-max-ch": `${maxCh}ch`,
  } as CSSProperties;
}

/** @deprecated Use getFieldWidthStyle */
export const getFieldMinWidthStyle = getFieldWidthStyle;

/** @deprecated Use getFieldWidthStyle with select: true */
export function getSelectFieldMinWidthStyle(
  placeholder?: string,
  label?: string,
  options?: Omit<FieldWidthOptions, "select">
): CSSProperties {
  return getFieldWidthStyle(placeholder, label, { ...options, select: true });
}
